/**
 * Quote System - Manages quote items in localStorage
 */
class QuoteUtils {
  static STORAGE_KEY = 'quote_items';

  /**
   * Get all quote items from localStorage
   * @returns {Array} Array of quote items
   */
  static getQuoteItems() {
    if (typeof Storage === 'undefined') return [];
    try {
      const items = localStorage.getItem(this.STORAGE_KEY);
      return items ? JSON.parse(items) : [];
    } catch (e) {
      console.error('Error reading quote items:', e);
      return [];
    }
  }

  /**
   * Save quote items to localStorage
   * @param {Array} items - Array of quote items
   */
  static saveQuoteItems(items) {
    if (typeof Storage === 'undefined') return;
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
      this.updateQuoteCount();
    } catch (e) {
      console.error('Error saving quote items:', e);
    }
  }

  /**
   * Add item to quote
   * @param {Object} item - Quote item object
   */
  static addToQuote(item) {
    const items = this.getQuoteItems();
    
    const existingIndex = items.findIndex(
      (i) => parseInt(i.variantId) === parseInt(item.variantId) && parseInt(i.productId) === parseInt(item.productId)
    );

    if (existingIndex > -1) {
      // Update quantity if item already exists
      items[existingIndex].quantity = (items[existingIndex].quantity || 1) + (item.quantity || 1);
    } else {
      // Add new item
      items.push({
        ...item,
        quantity: item.quantity || 1,
        addedAt: new Date().toISOString()
      });
    }

    this.saveQuoteItems(items);
    return items;
  }

  /**
   * Remove item from quote
   * @param {string|number} variantId - Variant ID to remove
   */
  static removeFromQuote(variantId) {
    const variantIdNum = parseInt(variantId);
    const items = this.getQuoteItems();
    const filtered = items.filter((item) => parseInt(item.variantId) !== variantIdNum);
    this.saveQuoteItems(filtered);
    return filtered;
  }

  /**
   * Update item quantity in quote
   * @param {string|number} variantId - Variant ID
   * @param {number} quantity - New quantity
   */
  static updateQuantity(variantId, quantity) {
    const variantIdNum = parseInt(variantId);
    const items = this.getQuoteItems();
    const item = items.find((i) => parseInt(i.variantId) === variantIdNum);
    if (item) {
      item.quantity = Math.max(1, quantity);
      this.saveQuoteItems(items);
    }
    return items;
  }

  /**
   * Clear all quote items
   */
  static clearQuote() {
    this.saveQuoteItems([]);
  }

  /**
   * Get quote item count
   * @returns {number} Total number of items in quote
   */
  static getQuoteCount() {
    const items = this.getQuoteItems();
    return items.reduce((total, item) => total + (item.quantity || 1), 0);
  }

  /**
   * Update quote count display in header/navigation
   */
  static updateQuoteCount() {
    const count = this.getQuoteCount();
    const countText = count < 100 ? count.toString() : '~';
    
    // Update all quote count spans
    const countSpans = document.querySelectorAll('.quote-count, .cart-count.quote-count, [data-quote-count]');
    countSpans.forEach((span) => {
      span.setAttribute('data-quote-count', count);
      
      // Find text nodes and visually-hidden span
      const hiddenSpan = span.querySelector('.visually-hidden');
      const textNodes = Array.from(span.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
      
      // Update or create text node with count
      if (textNodes.length > 0) {
        // Update first text node (the count number)
        textNodes[0].textContent = countText;
      } else {
        // No text node, create one
        if (hiddenSpan) {
          span.insertBefore(document.createTextNode(countText), hiddenSpan);
        } else {
          span.textContent = countText;
        }
      }
      
      // Ensure visually-hidden span exists
      if (!hiddenSpan) {
        const newHidden = document.createElement('span');
        newHidden.className = 'visually-hidden';
        newHidden.textContent = 'Quote items count';
        span.appendChild(newHidden);
      }
      
      span.style.display = '';
      span.style.visibility = '';
    });

    // Dispatch custom event for real-time updates
    document.dispatchEvent(new CustomEvent('quote:updated', { detail: { count, items: this.getQuoteItems() } }));
  }

  /**
   * Format price for display
   * @param {number} price - Price in cents
   * @returns {string} Formatted price
   */
  static formatPrice(price) {
    // Convert price to number if it's a string
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    const priceInCents = Math.round(priceNum || 0);
    
    // Try to use Shopify.formatMoney with money_format
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      const moneyFormat = window.cartStrings?.money_format || window.themeGlobalVariables?.settings?.money_format;
      if (moneyFormat) {
        try {
          return window.Shopify.formatMoney(priceInCents, moneyFormat);
        } catch (e) {
          // Fall through to simple formatting
        }
      }
    }
    
    // Simple fallback formatting
    const priceFloat = (priceInCents / 100).toFixed(2);
    return `$${priceFloat}`;
  }
}

/**
 * Add to Quote Button Handler
 */
class AddToQuoteButton extends HTMLElement {
  constructor() {
    super();
    this.button = null;
  }

  connectedCallback() {
    // Find the button - it could be the element itself or a child
    this.button = this.classList.contains('add-to-quote-btn') ? this : this.querySelector('.add-to-quote-btn');
    
    // If no button found, try to find any button with the data attributes
    if (!this.button) {
      this.button = this.querySelector('button[data-product-id], button[data-variant-id]');
    }
    
    // If still no button, check if this element itself has the data attributes
    if (!this.button && (this.dataset.productId || this.dataset.variantId)) {
      this.button = this;
    }
    
    if (this.button) {
      // Remove any existing listeners to avoid duplicates
      this.button.removeEventListener('click', this.handleClick);
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const targetButton = e.currentTarget;
    if (targetButton.disabled) return;

    // Try multiple ways to get quantity
    const quantityInput = targetButton.closest('.product-form__buttons')?.querySelector('.product-quantity-input') ||
                          targetButton.closest('.product-form')?.querySelector('input[name="quantity"]') ||
                          targetButton.closest('.product-item__product-form')?.querySelector('input[name="quantity"]') ||
                          targetButton.closest('.product-item')?.querySelector('.quantity-input') ||
                          document.querySelector(`input[name="quantity"][form*="${targetButton.closest('form')?.id || ''}"]`);
    
    const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value) || 1) : 1;

    // Get data attributes from button or from parent element
    const productId = targetButton.dataset.productId || targetButton.getAttribute('data-product-id') || this.dataset.productId;
    const variantId = targetButton.dataset.variantId || targetButton.getAttribute('data-variant-id') || this.dataset.variantId;
    const productTitle = targetButton.dataset.productTitle || targetButton.getAttribute('data-product-title') || this.dataset.productTitle || 'Product';
    const productUrl = targetButton.dataset.productUrl || targetButton.getAttribute('data-product-url') || this.dataset.productUrl || '#';
    const productPrice = targetButton.dataset.productPrice || targetButton.getAttribute('data-product-price') || this.dataset.productPrice || '0';
    const productImage = targetButton.dataset.productImage || targetButton.getAttribute('data-product-image') || this.dataset.productImage || '';

    if (!productId || !variantId) {
      console.error('[Quote] Missing product/variant ID');
      return;
    }

    // Show loading state
    const loadingIcon = targetButton.querySelector('.icon-load');
    const btnLabel = targetButton.querySelector('.btn-label, .hidden-on-load, span:not(.icon-load)');
    
    if (loadingIcon) loadingIcon.classList.remove('opacity-0');
    if (btnLabel) btnLabel.classList.add('hidden');
    targetButton.disabled = true;

    // Add to quote
    const quoteItem = {
      productId: parseInt(productId),
      variantId: parseInt(variantId),
      title: productTitle || 'Product',
      url: productUrl || '#',
      price: parseInt(productPrice) || 0,
      image: productImage || '',
      quantity: quantity
    };
    
    QuoteUtils.addToQuote(quoteItem);

    // Reset button state after short delay
    setTimeout(() => {
      if (loadingIcon) loadingIcon.classList.add('opacity-0');
      if (btnLabel) {
        btnLabel.textContent = 'Added to Quote';
        btnLabel.classList.remove('hidden');
      }
      targetButton.disabled = false;

      // Reset text after 2 seconds
      setTimeout(() => {
        if (btnLabel) {
          btnLabel.textContent = 'Add to Quote';
        }
      }, 2000);

      // Show notification if available
      const notification = document.querySelector('quote-notification');
      if (notification) {
        notification.show(productTitle || 'Product');
      }
    }, 500);
  }
}

/**
 * Quote Notification Component
 */
class QuoteNotification extends HTMLElement {
  show(productName) {
    const message = document.createElement('div');
    message.className = 'quote-notification fixed top-20 right-20 bg-success color-white p-15 rounded shadow z-50';
    message.innerHTML = `
      <div class="flex align-center gap-10">
        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
          <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-2 15l-5-5 1.41-1.41L8 12.17l7.59-7.59L17 6l-9 9z" fill="currentColor"/>
        </svg>
        <span>${productName} added to quote</span>
      </div>
    `;
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.opacity = '0';
      message.style.transition = 'opacity 0.3s';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }
}

// Register custom elements
if (!customElements.get('add-to-quote-button')) {
  customElements.define('add-to-quote-button', AddToQuoteButton);
}
if (!customElements.get('quote-notification')) {
  customElements.define('quote-notification', QuoteNotification);
}

// Initialize quote count on page load and handle buttons
document.addEventListener('DOMContentLoaded', () => {
  QuoteUtils.updateQuoteCount();
  
  // Handle add to quote buttons that aren't wrapped in custom element
  const buttons = document.querySelectorAll('.add-to-quote-btn:not(add-to-quote-button .add-to-quote-btn)');
  buttons.forEach((btn) => {
    // Check if button is already inside an add-to-quote-button element
    if (!btn.closest('add-to-quote-button')) {
      // Check if button has required data attributes
      if (btn.dataset.productId && btn.dataset.variantId) {
        const wrapper = document.createElement('add-to-quote-button');
        btn.parentNode.insertBefore(wrapper, btn);
        wrapper.appendChild(btn);
      } else {
        // If button doesn't have data attributes, try to find them from parent form/context
        const form = btn.closest('form');
        const productForm = btn.closest('.product-form, product-form');
        if (form || productForm) {
          const idInput = (form || productForm).querySelector('input[name="id"]');
          const variantId = idInput ? idInput.value : null;
          
          // Try to get product ID from URL or other sources
          const productId = btn.dataset.productId || 
                           btn.getAttribute('data-product-id') ||
                           (productForm ? productForm.dataset.productId : null);
          
          if (productId && variantId) {
            btn.setAttribute('data-product-id', productId);
            btn.setAttribute('data-variant-id', variantId);
            const wrapper = document.createElement('add-to-quote-button');
            btn.parentNode.insertBefore(wrapper, btn);
            wrapper.appendChild(btn);
          }
        }
      }
    }
  });
});

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.QuoteUtils = QuoteUtils;
}

